import { HedgeDeltaDenomination, HedgeMechanism } from '@/types/generated/algo'

export type HedgeScopeMode = 'portfolio' | 'underlying' | 'positionGroup'
export type HedgeModeKind = 'deltaStream' | 'interval'
// Price Range configures what price domain the *chart* simulates the position's delta over —
// same role as risk-slides' priceRangeFrom/To/Kind. It is display-only: it never touches
// deltaBands (the real hedge trigger values, always literal numbers in `denomination` units)
// and is never sent to the backend. Only meaningful for a single-underlying Target — there's
// no one price to simulate against for a whole-portfolio scope.
export type HedgePriceRangeKind = 'absolute' | 'percent'

export interface HedgeBandForm {
  enabled: boolean
  from: string
  to: string
  target: string
}

export interface HedgePriorityBandForm extends HedgeBandForm {
  mechanism: HedgeMechanism
  twapDurationSeconds: string
}

export const PRIORITY_RUNG_KEYS = [
  'minHighPriority',
  'minMediumPriority',
  'minLowPriority',
  'maxLowPriority',
  'maxMediumPriority',
  'maxHighPriority',
] as const

export type PriorityRungKey = (typeof PRIORITY_RUNG_KEYS)[number]

// Full ladder order (lower-bound ascending), acceptable in the middle.
export const RUNG_ORDER = [
  'minHighPriority',
  'minMediumPriority',
  'minLowPriority',
  'acceptable',
  'maxLowPriority',
  'maxMediumPriority',
  'maxHighPriority',
] as const

export type RungKey = (typeof RUNG_ORDER)[number]

export interface HedgeFormModel {
  hedgeInstrument: string
  scopeMode: HedgeScopeMode
  underlyingAsset: string
  positionSymbols: string[]
  positionGroupLabel: string
  denomination: HedgeDeltaDenomination
  // Chart-simulation-only price bounds — see HedgePriceRangeKind. Not part of deltaBands.
  priceRangeKind: HedgePriceRangeKind
  priceRangeFrom: string
  priceRangeTo: string
  // Simulation grid resolution — same option set as risk-slides' priceStep ('10 Parts' etc.),
  // parsed the same way (parseInt(...) || 10) when building the simulation request.
  priceStep: string
  acceptable: HedgeBandForm
  minHighPriority: HedgePriorityBandForm
  minMediumPriority: HedgePriorityBandForm
  minLowPriority: HedgePriorityBandForm
  maxLowPriority: HedgePriorityBandForm
  maxMediumPriority: HedgePriorityBandForm
  maxHighPriority: HedgePriorityBandForm
  modeKind: HedgeModeKind
  // Interval duration in seconds. Ignored in delta-stream mode, which uses server defaults.
  executionSeconds: string
  positiveGammaTreatment: boolean
  replaceOverlapping: boolean
}

export interface HedgeValidationError {
  field: string
  message: string
}
